import React from 'react';

import {
  buttonBaseClasses,
  buttonColorVariants,
  buttonSizeVariants
} from '@/constants/buttonStyles';
import { Color } from '@/constants/color';

import Spinner from '../Spinner';

import type { Props } from './types';

const Button = ({
  ariaLabel,
  children,
  className,
  color = Color.BLUE,
  disabled = false,
  href,
  loading = false,
  loadingWithSpinner = true,
  spinnerVisibleLabel = true,
  onClick,
  rel = 'noreferrer nofollow',
  size = 'base',
  target,
  title,
  type = 'button',
  dataTestId
}: Props) => {
  const classNames = `rounded ${buttonBaseClasses} ${buttonColorVariants[color as Color]} ${className} ${
    buttonSizeVariants[size]
  }`;

  const disabledClassNames = 'opacity-50 cursor-not-allowed';
  const combinedClassNames =
    disabled || loading ? `${classNames} ${disabledClassNames}` : classNames;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (!disabled && !loading && onClick) {
      onClick(event);
    }
  };

  const content = loading ? (
    loadingWithSpinner ? (
      <Spinner color={color} visibleLabel={spinnerVisibleLabel} />
    ) : (
      'Loading...'
    )
  ) : (
    children
  );

  if (href) {
    return (
      <a
        aria-label={ariaLabel}
        className={combinedClassNames}
        href={disabled || loading ? '#' : href}
        data-testid={dataTestId}
        onClick={handleClick}
        rel={rel}
        target={target}
        title={title}>
        {content}
      </a>
    );
  } else {
    return (
      <button
        aria-label={ariaLabel}
        className={combinedClassNames}
        disabled={disabled || loading}
        data-testid={dataTestId}
        onClick={handleClick}
        type={type}
        title={title}>
        {content}
      </button>
    );
  }
};
export default React.memo(Button);
